import { Component } from '@theme/component';

import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 * @extends {Component<Refs>}
 */

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** @type {HTMLElement[] | null} */
  #resultsItemsCache = null;

  /** @type {string | null} */
  #emptyStateMarkupCache = null;

  /** @type {boolean} */
  #isKeyboardNavigation = false;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      const loadWhenIdle = () => this.#loadEmptyState();

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(loadWhenIdle, { timeout: 1500 });
      } else {
        window.setTimeout(loadWhenIdle, 500);
      }
    }
  }

  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#activeFetch?.abort();
    this.#resultsItemsCache = null;
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;

    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  #invalidateResultsCache() {
    this.#resultsItemsCache = null;
  }

  get #allResultsItems() {
    if (this.#resultsItemsCache) return this.#resultsItemsCache;

    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    this.#resultsItemsCache = /** @type {HTMLElement[]} */ (
      containers
        .flatMap((container) => {
          if (container.classList.contains('predictive-search-results__wrapper-products')) {
            return Array.from(
              container.querySelectorAll('.predictive-search-results__card')
            );
          }

          return Array.from(
            container.querySelectorAll(
              '[ref="resultsItems[]"], .predictive-search-results__card'
            )
          );
        })
        .filter((item) => item instanceof HTMLElement)
    );

    return this.#resultsItemsCache;
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex(
      (item) => item.getAttribute('aria-selected') === 'true'
    );
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;

    if (!items.length) return;

    let activeItem = null;

    for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
      const item = items[itemIndex];

      item.classList.remove('keyboard-focus');

      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }

        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    const items = this.#allResultsItems;

    const index = items.findIndex(
      (item) => item.getAttribute('aria-selected') === 'true'
    );

    return index >= 0 ? items[index] : undefined;
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    const items = this.#allResultsItems;

    if (
      !items.length ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      return;
    }

    const currentIndex = items.findIndex(
      (item) => item.getAttribute('aria-selected') === 'true'
    );

    const totalItems = items.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        this.#currentIndex =
          currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;

        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        if (event.shiftKey) {
          this.#currentIndex =
            currentIndex > 0
              ? currentIndex - 1
              : totalItems - 1;
        } else {
          this.#currentIndex =
            currentIndex < totalItems - 1
              ? currentIndex + 1
              : 0;
        }

        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        this.#currentIndex =
          currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1;

        break;

      case 'Enter': {
        const singleResultContainer =
          this.refs.predictiveSearchResults.querySelector(
            '[data-single-result-url]'
          );

        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();

          window.location.href =
            singleResultContainer.dataset.singleResultUrl;

          return;
        }

        if (currentIndex >= 0) {
          event.preventDefault();

          items[currentIndex]
            ?.querySelector('a')
            ?.click();
        } else {
          const searchUrl = new URL(
            Theme.routes.search_url,
            location.origin
          );

          searchUrl.searchParams.set(
            'q',
            this.refs.searchInput.value
          );

          window.location.href = searchUrl.toString();
        }

        break;
      }
    }
  };

  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();
    this.#emptyStateMarkupCache = null;

    const {
      recentlyViewedItems,
      recentlyViewedTitle,
      recentlyViewedWrapper,
    } = this.refs;

    const allRecentlyViewedElements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (allRecentlyViewedElements.length === 0) return;

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
        this.#invalidateResultsCache();
      });
    }
  }

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }

    this.#resetSearch();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm =
      this.refs.searchInput.value.trim();

    this.#isKeyboardNavigation = false;
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults
        .querySelector(
          '.predictive-search-results__inner'
        )
        ?.scrollTo(0, 0);

      this.querySelector(
        '.predictive-search-form__content'
      )?.scrollTo(0, 0);
    });
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(
      Theme.routes.predictive_search_url,
      location.origin
    );

    url.searchParams.set('q', searchTerm);
    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    const { predictiveSearchResults } = this.refs;

    const abortController =
      this.#createAbortController();

    const deferredPromise =
      SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(
        this.dataset.sectionId,
        false,
        url
      )
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({
            totalCount: 0,
          });

          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(
            new Error('Fetch aborted by user')
          );

          return;
        }

        this.#invalidateResultsCache();

        morph(
          predictiveSearchResults,
          resultsMarkup
        );

        this.#invalidateResultsCache();

        this.#resetScrollPositions();

        const resultCount =
          predictiveSearchResults.querySelectorAll(
            '[ref="resultsItems[]"]'
          ).length;

        deferredPromise.resolve({
          totalCount: resultCount,
        });
      })
      .catch((error) => {
        deferredPromise.reject(error);

        if (abortController.signal.aborted) {
          return;
        }

        throw error;
      });
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts =
      RecentlyViewed.getProducts();

    if (viewedProducts.length === 0) {
      return null;
    }

    const url = new URL(
      Theme.routes.search_url,
      location.origin
    );

    url.searchParams.set(
      'q',
      viewedProducts
        .map((id) => `id:${id}`)
        .join(' OR ')
    );

    url.searchParams.set(
      'resources[type]',
      'product'
    );

    return sectionRenderer.getSectionHTML(
      this.dataset.sectionId,
      false,
      url
    );
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController =
      new AbortController();

    this.#activeFetch?.abort();

    this.#activeFetch = abortController;

    return abortController;
  }

  #resetSearch = async () => {
    const {
      predictiveSearchResults,
      searchInput,
    } = this.refs;

    this.#isKeyboardNavigation = false;
    this.#currentIndex = -1;

    searchInput.value = '';

    this.#hideResetButton();

    const abortController =
      this.#createAbortController();

    if (this.#emptyStateMarkupCache) {
      if (abortController.signal.aborted) {
        return;
      }

      const cachedMarkup =
        new DOMParser()
          .parseFromString(
            this.#emptyStateMarkupCache,
            'text/html'
          )
          .querySelector(
            '.predictive-search-empty-section'
          );

      if (cachedMarkup) {
        this.#invalidateResultsCache();

        morph(
          predictiveSearchResults,
          cachedMarkup
        );

        this.#invalidateResultsCache();

        this.#resetScrollPositions();

        return;
      }

      this.#emptyStateMarkupCache = null;
    }

    const emptySectionId =
      'predictive-search-empty';

    const url =
      new URL(window.location.href);

    url.searchParams.delete('page');

    const emptySectionMarkup =
      await sectionRenderer.getSectionHTML(
        emptySectionId,
        false,
        url
      );

    if (abortController.signal.aborted) {
      return;
    }

    const parsedEmptySectionMarkup =
      new DOMParser()
        .parseFromString(
          emptySectionMarkup,
          'text/html'
        )
        .querySelector(
          '.predictive-search-empty-section'
        );

    if (!parsedEmptySectionMarkup) {
      throw new Error(
        'No empty section markup found'
      );
    }

    const viewedProducts =
      RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup =
        await this.#getRecentlyViewedProductsMarkup();

      if (
        abortController.signal.aborted ||
        !recentlyViewedMarkup
      ) {
        return;
      }

      const parsedRecentlyViewedMarkup =
        new DOMParser().parseFromString(
          recentlyViewedMarkup,
          'text/html'
        );

      const recentlyViewedProductsHtml =
        parsedRecentlyViewedMarkup.getElementById(
          'predictive-search-products'
        );

      if (!recentlyViewedProductsHtml) {
        return;
      }

      for (
        const child of
        recentlyViewedProductsHtml.children
      ) {
        if (child instanceof HTMLElement) {
          child.setAttribute(
            'ref',
            'recentlyViewedWrapper'
          );
        }
      }

      const collectionElement =
        parsedEmptySectionMarkup.querySelector(
          '#predictive-search-products'
        );

      if (!collectionElement) {
        return;
      }

      collectionElement.prepend(
        ...recentlyViewedProductsHtml.children
      );
    }

    if (abortController.signal.aborted) {
      return;
    }

    this.#emptyStateMarkupCache =
      parsedEmptySectionMarkup.outerHTML;

    this.#invalidateResultsCache();

    morph(
      predictiveSearchResults,
      parsedEmptySectionMarkup
    );

    this.#invalidateResultsCache();

    this.#resetScrollPositions();
  };
}

if (
  !customElements.get(
    'predictive-search-component'
  )
) {
  customElements.define(
    'predictive-search-component',
    PredictiveSearchComponent
  );
}